import React, { useState, useEffect } from "react";
import { Button, FormInput } from "../Common.jsx";
import { DynamicIcon } from "../icons.jsx";
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowLeft, Save } from 'lucide-react';

import BrandingVisualsTab from "./BrandingVisualsTab.jsx";
import BrandingThemeTab from "./BrandingThemeTab.jsx";
import BrandingEmailTab from "./BrandingEmailTab.jsx";
import BrandingDomainTab from "./BrandingDomainTab.jsx";
import BrandingAdvancedTab from "./BrandingAdvancedTab.jsx";
import { uploadBrandingAsset } from "../../api/index.js";
import { generateSecondaryColor } from "../../utils/colorUtils";

export default function BrandingForm({ tenantId, data, token, onSave, onSavePartial, isLastStep, saving }) {
  const [activeTab, setActiveTab] = useState("visuals");
  const [isPartialSaving, setIsPartialSaving] = useState(false);
  
  const defaultPrimary = "#3B82F6";
  const [form, setForm] = useState({
    logo_light_url: "",
    logo_dark_url: "",

    collapsed_logo_url: "",
    theme_color: defaultPrimary,
    secondary_color: generateSecondaryColor(defaultPrimary),
    font_family: "Inter",
    border_radius: 12,
    theme_mode: "system",
    email_sender_name: "",
    email_reply_to: "",
    email_logo_url: "",
    email_footer: "",
    email_template: "default",
    email_title: "",
    email_button_text: "",
    email_button_align: "left",
    email_button_color: "",
    email_custom_html: "",
    custom_domain: "",
    privacy_policy_url: "",
    terms_of_service_url: "",
    support_email: "",
    support_url: "",
    cookie_policy_url: "",
    dpa_url: "",
    watermark_enabled: true,
    watermark_type: "text",
    watermark_text: "",
    watermark_position: "bottom-right",
    watermark_opacity: 30,
    powered_by_enabled: true,

    chart_theme: "default",
    custom_login_message: "",
    custom_css: ""
  });

  useEffect(() => {
    if (data) {
      setForm(prev => ({
        ...prev,
        ...data,
        // Unpack nested objects
        ...(data.theme_config || {}),
        ...(data.watermark_config || {}),
        ...(data.email_config || {})
      }));
    }
  }, [data]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleFileUpload = async (key, file, type) => {
    try {
      const res = await uploadBrandingAsset(tenantId, token, type, file);
      update(key, res.url);
      return res.url;
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Failed to upload image: " + err.message);
    }
  };

  const tabs = [
    { id: 'visuals', label: 'Visuals' },
    { id: 'theme', label: 'Theme' },
    { id: 'email', label: 'Email' },
   /*  { id: 'domain', label: 'Domain & Links' }, */
    { id: 'advanced', label: 'Advanced' }
  ];

  const currentTabIndex = tabs.findIndex(t => t.id === activeTab);
  const isLastSubTab = currentTabIndex === tabs.length - 1;

  const handleBack = () => {
    if (currentTabIndex > 0) {
      setActiveTab(tabs[currentTabIndex - 1].id);
    }
  };

  const getPayload = () => {
    return {
      logo_light_url: form.logo_light_url,
      logo_dark_url: form.logo_dark_url,

      collapsed_logo_url: form.collapsed_logo_url,
      custom_domain: form.custom_domain,
      custom_css: form.custom_css,
      timezone: form.timezone,
      theme_config: {
        theme_color: form.theme_color,
        secondary_color: form.secondary_color,
        font_family: form.font_family,
        border_radius: form.border_radius,
        theme_mode: form.theme_mode,

        chart_theme: form.chart_theme
      },
      watermark_config: {
        watermark_enabled: form.watermark_enabled,
        watermark_type: form.watermark_type,
        watermark_text: form.watermark_text,
        watermark_position: form.watermark_position,
        watermark_opacity: form.watermark_opacity,
        powered_by_enabled: form.powered_by_enabled
      },
      email_config: {
        email_sender_name: form.email_sender_name,
        email_reply_to: form.email_reply_to,
        email_logo_url: form.email_logo_url,
        email_footer: form.email_footer,
        email_template: form.email_template,
        email_title: form.email_title,
        email_button_text: form.email_button_text,
        email_button_align: form.email_button_align,
        email_button_color: form.email_button_color,
        email_custom_html: form.email_custom_html
      }
    };
  };

  const handleAction = async () => {
    const finalPayload = getPayload();
    
    if (!isLastSubTab) {
      if (onSavePartial) {
        setIsPartialSaving(true);
        try {
          await onSavePartial(finalPayload);
        } catch (err) {
          console.error("Partial save failed:", err);
          setIsPartialSaving(false);
          return; // Stop navigation on error
        }
        setIsPartialSaving(false);
      }
      setActiveTab(tabs[currentTabIndex + 1].id);
    } else {
      onSave(finalPayload);
    }
  };

  return (
    <div className="w-full flex-1 flex flex-col h-full bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      
      {/* Header & Tabs */}
      <div className="shrink-0 z-30 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 pt-6 pb-0 rounded-t-xl">
        <div className="flex gap-10 overflow-x-auto">
           {tabs.map(tab => (
             <button
               key={tab.id}
               onClick={() => setActiveTab(tab.id)}
               className={`no-branding-radius pb-4 text-[15px] font-medium transition-all duration-200 relative whitespace-nowrap ${
                 activeTab === tab.id 
                   ? 'text-blue-600 dark:text-blue-400 font-semibold' 
                   : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
               }`}
             >
               {tab.label}
               {activeTab === tab.id && (
                 <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600 dark:bg-blue-500 rounded-t-full shadow-[0_-2px_4px_rgba(37,99,235,0.2)]" />
               )}
             </button>
           ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-8 pt-6">
         {activeTab === 'visuals' && <BrandingVisualsTab form={form} update={update} onUpload={handleFileUpload} />}
         {activeTab === 'theme' && <BrandingThemeTab form={form} update={update} />}
         {activeTab === 'email' && <BrandingEmailTab form={form} update={update} onUpload={handleFileUpload} />}
         {activeTab === 'domain' && <BrandingDomainTab form={form} update={update} />}
         {activeTab === 'advanced' && <BrandingAdvancedTab form={form} update={update} />}
      </div>

      {/* Footer */}
      <div className="shrink-0 z-20 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-6 flex justify-between items-center rounded-b-xl">
        <div>
          {currentTabIndex > 0 && (
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 px-5 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-all focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-800"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
        </div>

        <Button
          onClick={handleAction}
          disabled={saving || isPartialSaving}
          className={`bg-blue-600 hover:bg-blue-700 text-white px-8 py-2.5 rounded-lg shadow-md shadow-blue-500/30 font-semibold transition-all transform hover:-translate-y-0.5 focus:ring-4 focus:ring-blue-500/20 ${(isLastStep && isLastSubTab) ? 'min-w-[170px]' : ''}`}
        >
          {(saving || isPartialSaving) ? (
            <div className="flex items-center justify-center">
              <CircularProgress size={16} className="mr-2 text-white" color="inherit" />
              {(isLastStep && isLastSubTab) ? "Provisioning..." : "Saving..."}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <Save className="w-4 h-4" /> 
              {isLastSubTab ? (isLastStep ? "Save & Finish" : "Save Changes") : "Save & Next"} 
              {(isLastStep && isLastSubTab) && <DynamicIcon name="CheckCircle" className="w-4 h-4" />}
            </div>
          )}
        </Button>
      </div>

    </div>
  );
}
